/** FELVORA — Inmobiliaria F7 P4 · Estado de cuenta al propietario (PDF). */
import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { FileText, Plus, Download, Send, CheckCircle2, RefreshCcw, Layers } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const mxn = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

const badgeEstado = (e) => {
  if (e === "borrador") return "bg-slate-100 text-slate-700";
  if (e === "emitido") return "bg-sky-100 text-sky-700";
  if (e === "entregado") return "bg-emerald-100 text-emerald-700";
  return "bg-slate-100 text-slate-700";
};

const hoyPeriodo = () => new Date().toISOString().slice(0, 7);

export default function EstadosCuenta() {
  const [items, setItems] = useState([]);
  const [propietarios, setPropietarios] = useState([]);
  const [periodo, setPeriodo] = useState(hoyPeriodo());
  const [propFilter, setPropFilter] = useState("todos");
  const [estadoFilter, setEstadoFilter] = useState("todos");
  const [openNuevo, setOpenNuevo] = useState(false);
  const [nuevoProp, setNuevoProp] = useState("");
  const [openDetalle, setOpenDetalle] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [openEntrega, setOpenEntrega] = useState(false);
  const [entrega, setEntrega] = useState({ canal: "correo", notas: "" });
  const [busy, setBusy] = useState(false);

  const cargar = () => {
    const params = { periodo };
    if (propFilter !== "todos") params.propietario_cliente_id = propFilter;
    if (estadoFilter !== "todos") params.estado = estadoFilter;
    api.get("/inmobiliaria/estados-cuenta", { params }).then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => { cargar(); }, [periodo, propFilter, estadoFilter]);
  useEffect(() => {
    // Solo propietarios (clientes referenciados en inmuebles)
    api.get("/inmobiliaria/inmuebles").then((r) => {
      const map = new Map();
      (r.data || []).forEach((i) => {
        if (i.propietario_cliente_id && !map.has(i.propietario_cliente_id)) {
          map.set(i.propietario_cliente_id, {
            id: i.propietario_cliente_id,
            nombre: i.propietario_nombre_snap || "(sin nombre)",
          });
        }
      });
      setPropietarios(Array.from(map.values()));
    }).catch(() => {});
  }, []);

  const generarUno = async () => {
    if (!nuevoProp) return toast.error("Selecciona un propietario.");
    setBusy(true);
    try {
      await api.post("/inmobiliaria/estados-cuenta/generar", {
        propietario_cliente_id: nuevoProp, periodo,
      });
      toast.success("Estado de cuenta generado (borrador)");
      setOpenNuevo(false); setNuevoProp("");
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const generarTodos = async () => {
    setBusy(true);
    try {
      const r = await api.post("/inmobiliaria/estados-cuenta/generar-todos-del-mes", { periodo });
      toast.success(`Generados: ${r.data.creados?.length || 0} · Propietarios con actividad: ${r.data.total_propietarios || 0}`);
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const verDetalle = async (id) => {
    try {
      const r = await api.get(`/inmobiliaria/estados-cuenta/${id}`);
      setDetalle(r.data); setOpenDetalle(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const emitir = async (id) => {
    try {
      await api.post(`/inmobiliaria/estados-cuenta/${id}/emitir`);
      toast.success("Estado emitido"); cargar();
      if (detalle?.id === id) verDetalle(id);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const marcarEntregado = async () => {
    try {
      await api.post(`/inmobiliaria/estados-cuenta/${detalle.id}/entregar`, entrega);
      toast.success("Marcado como entregado");
      setOpenEntrega(false); cargar(); verDetalle(detalle.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const descargarPDF = async (id) => {
    try {
      const r = await api.get(`/inmobiliaria/estados-cuenta/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `estado-cuenta-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo descargar el PDF"); }
  };

  const propietarioNombre = (pid) =>
    propietarios.find((p) => p.id === pid)?.nombre || pid?.slice(0, 8) || "—";

  const totales = useMemo(() => {
    const t = { ingresos: 0, egresos: 0, honorarios: 0, neto: 0 };
    items.forEach((it) => {
      const to = it.detalle?.totales || {};
      t.ingresos += to.ingresos || 0;
      t.egresos += to.egresos_propietario || 0;
      t.honorarios += to.honorarios_admin || 0;
      t.neto += to.neto || 0;
    });
    return t;
  }, [items]);

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="estados-cuenta-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">
            <FileText className="mr-2 inline h-6 w-6 text-indigo-700" />
            Estado de cuenta al propietario
          </h1>
          <p className="text-sm text-slate-500">
            Consolidado por propietario · ingresos − egresos − honorarios administrativos = neto a liquidar.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setOpenNuevo(true)} data-testid="btn-generar-uno">
            <Plus className="mr-1 h-4 w-4" /> Generar (uno)
          </Button>
          <Button onClick={generarTodos} disabled={busy} data-testid="btn-generar-todos">
            <Layers className="mr-1 h-4 w-4" /> Generar todos del mes
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-4 md:grid-cols-4">
          <div>
            <label className="text-xs text-slate-500">Periodo (YYYY-MM)</label>
            <Input type="month" value={periodo} onChange={(e) => setPeriodo(e.target.value)} data-testid="input-periodo" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Propietario</label>
            <Select value={propFilter} onValueChange={setPropFilter}>
              <SelectTrigger data-testid="select-prop-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {propietarios.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Estado</label>
            <Select value={estadoFilter} onValueChange={setEstadoFilter}>
              <SelectTrigger data-testid="select-estado-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="borrador">Borrador</SelectItem>
                <SelectItem value="emitido">Emitido</SelectItem>
                <SelectItem value="entregado">Entregado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button variant="outline" onClick={cargar} className="w-full"><RefreshCcw className="mr-1 h-4 w-4" /> Refrescar</Button>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Ingresos</div><div className="text-xl font-semibold text-emerald-700" data-testid="kpi-ingresos">{mxn(totales.ingresos)}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Egresos</div><div className="text-xl font-semibold text-rose-700" data-testid="kpi-egresos">{mxn(totales.egresos)}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Honorarios admin.</div><div className="text-xl font-semibold text-amber-700" data-testid="kpi-honorarios">{mxn(totales.honorarios)}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Neto</div><div className="text-xl font-semibold text-indigo-700" data-testid="kpi-neto">{mxn(totales.neto)}</div></CardContent></Card>
      </div>

      {/* Bandeja */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Bandeja mensual · {items.length} estados</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="tabla-estados">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="p-2">Propietario</th>
                <th className="p-2">Periodo</th>
                <th className="p-2">Estado</th>
                <th className="p-2 text-right">Ingresos</th>
                <th className="p-2 text-right">Egresos</th>
                <th className="p-2 text-right">Honorarios</th>
                <th className="p-2 text-right">Neto</th>
                <th className="p-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan="8" className="p-4 text-center text-slate-500">Sin estados en este periodo.</td></tr>
              )}
              {items.map((it) => {
                const t = it.detalle?.totales || {};
                return (
                  <tr key={it.id} className="border-t hover:bg-slate-50" data-testid={`row-${it.id}`}>
                    <td className="p-2 font-medium">{propietarioNombre(it.propietario_cliente_id)}</td>
                    <td className="p-2">{it.periodo}</td>
                    <td className="p-2"><Badge className={badgeEstado(it.estado)} data-testid={`badge-${it.id}`}>{it.estado}</Badge></td>
                    <td className="p-2 text-right text-emerald-700">{mxn(t.ingresos)}</td>
                    <td className="p-2 text-right text-rose-700">{mxn(t.egresos_propietario)}</td>
                    <td className="p-2 text-right text-amber-700">{mxn(t.honorarios_admin)}</td>
                    <td className="p-2 text-right font-semibold">{mxn(t.neto)}</td>
                    <td className="p-2 space-x-1">
                      <Button size="sm" variant="outline" onClick={() => verDetalle(it.id)} data-testid={`btn-ver-${it.id}`}>Ver</Button>
                      <Button size="sm" variant="outline" onClick={() => descargarPDF(it.id)} data-testid={`btn-pdf-${it.id}`}><Download className="h-3 w-3" /></Button>
                      {it.estado === "borrador" && (
                        <Button size="sm" onClick={() => emitir(it.id)} data-testid={`btn-emitir-${it.id}`}><Send className="mr-1 h-3 w-3" /> Emitir</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Modal generar uno */}
      <Dialog open={openNuevo} onOpenChange={setOpenNuevo}>
        <DialogContent data-testid="dialog-generar-uno">
          <DialogHeader><DialogTitle>Generar estado de cuenta</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500">Propietario</label>
              <Select value={nuevoProp} onValueChange={setNuevoProp}>
                <SelectTrigger data-testid="select-nuevo-prop"><SelectValue placeholder="Elige un propietario" /></SelectTrigger>
                <SelectContent>
                  {propietarios.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Periodo</label>
              <Input type="month" value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNuevo(false)}>Cancelar</Button>
            <Button onClick={generarUno} disabled={busy} data-testid="btn-confirmar-generar">Generar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal detalle */}
      <Dialog open={openDetalle} onOpenChange={setOpenDetalle}>
        <DialogContent className="max-w-3xl" data-testid="dialog-detalle">
          <DialogHeader><DialogTitle>Estado de cuenta · {detalle?.periodo}</DialogTitle></DialogHeader>
          {detalle && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <div className="text-sm text-slate-500">Propietario</div>
                <div className="font-medium">{propietarioNombre(detalle.propietario_cliente_id)}</div>
                <div className="text-xs text-slate-500 mt-1">
                  Rango: {detalle.desde} → {detalle.hasta} · Estado: <Badge className={badgeEstado(detalle.estado)}>{detalle.estado}</Badge>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2 text-sm">
                <div className="rounded bg-emerald-50 p-2"><div className="text-xs text-emerald-700">Ingresos</div><div className="font-semibold">{mxn(detalle.detalle?.totales.ingresos)}</div></div>
                <div className="rounded bg-rose-50 p-2"><div className="text-xs text-rose-700">Egresos</div><div className="font-semibold">{mxn(detalle.detalle?.totales.egresos_propietario)}</div></div>
                <div className="rounded bg-amber-50 p-2"><div className="text-xs text-amber-700">Honorarios</div><div className="font-semibold">{mxn(detalle.detalle?.totales.honorarios_admin)}</div></div>
                <div className="rounded bg-indigo-50 p-2"><div className="text-xs text-indigo-700">Neto</div><div className="font-semibold">{mxn(detalle.detalle?.totales.neto)}</div></div>
              </div>
              <div className="space-y-2">
                {(detalle.detalle?.inmuebles || []).map((b) => (
                  <div key={b.inmueble_id} className="rounded border p-3" data-testid={`inmueble-block-${b.inmueble_id}`}>
                    <div className="font-medium">{b.direccion} <span className="text-xs text-slate-500">({b.tipo})</span></div>
                    <div className="text-sm mt-1">
                      Ingresos: <b>{mxn(b.ingresos)}</b> · Egresos: <b>{mxn(b.egresos_propietario)}</b> · Honorarios: <b>{mxn(b.honorarios_admin.monto)}</b> ({b.honorarios_admin.tipo})
                    </div>
                    <div className="text-sm font-semibold text-indigo-700">Neto: {mxn(b.neto)}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button onClick={() => descargarPDF(detalle.id)} data-testid="btn-detalle-pdf"><Download className="mr-1 h-4 w-4" /> Descargar PDF</Button>
                {detalle.estado === "borrador" && (
                  <Button onClick={() => emitir(detalle.id)} data-testid="btn-detalle-emitir"><Send className="mr-1 h-4 w-4" /> Emitir</Button>
                )}
                {detalle.estado === "emitido" && (
                  <Button onClick={() => setOpenEntrega(true)} data-testid="btn-detalle-entregar"><CheckCircle2 className="mr-1 h-4 w-4" /> Marcar entregado</Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal entregar */}
      <Dialog open={openEntrega} onOpenChange={setOpenEntrega}>
        <DialogContent data-testid="dialog-entregar">
          <DialogHeader><DialogTitle>Marcar como entregado</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500">Canal</label>
              <Select value={entrega.canal} onValueChange={(v) => setEntrega({ ...entrega, canal: v })}>
                <SelectTrigger data-testid="select-canal"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="correo">Correo</SelectItem>
                  <SelectItem value="impreso">Impreso</SelectItem>
                  <SelectItem value="portal">Portal</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Notas</label>
              <Textarea value={entrega.notas} onChange={(e) => setEntrega({ ...entrega, notas: e.target.value })} placeholder="Ej. Se envió a maria@correo.com el 15/07/2026." data-testid="input-notas-entrega" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenEntrega(false)}>Cancelar</Button>
            <Button onClick={marcarEntregado} data-testid="btn-confirmar-entrega">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
